import { useState } from 'react'

import type { ShipmentStatusEnum, TShipmentMember } from '../../../../../types/shipment'
import type { UserDetails } from '../../../../../types/user'
import type { IWorkspace } from '../../../../../types/workspace'
import { EmailPhoneForm } from '../../shared/email-phone-form'
import { useDispatchersList } from './hooks/use-dispatchers-list'
import { useDispatcherMutations } from './hooks/use-dispatchers-mutations'
import { useMemberSearch } from './hooks/use-member-search'
import { MemberSelectionList } from './member-selection-list'
import { WorkspaceListModal } from './workspace-list-modal'

interface IDispatchersList {
  dispatchers: TShipmentMember[]
  onClose: () => void
  shipmentID: string
  shipmentNumber: string
  shipmentStatus: ShipmentStatusEnum
  workspaceID: string
}

/**
 * This component is used to display the list of dispatchers in the shipment.
 */
export const DispatchersList = ({
  dispatchers,
  onClose,
  shipmentID,
  shipmentNumber,
  shipmentStatus,
  workspaceID
}: IDispatchersList) => {
  const dispatchersList = dispatchers.map((dispatcher) => {
    return {
      ...dispatcher.user,
      workspaceID: dispatcher.workspace?.id
    }
  })

  const dispatchersWorkspace = dispatchers
    .map(({ workspace }) => workspace)
    .filter((workspace): workspace is IWorkspace => !!workspace)

  const [openInvitePopup, setOpenInvitePopup] = useState(false)

  const {
    handleAddDispatcher,
    handleRemoveDispatcher,
    isLoading: mutationLoading
  } = useDispatcherMutations(dispatchers, onClose, shipmentID, shipmentNumber, shipmentStatus, workspaceID, () => {
    setOpenInvitePopup(false)
  })

  const {
    handleSubmit,
    loading,
    dispatcherWorkspaceList,
    handleAssignDispatcher,
    resetInviteState,
    setUserInput,
    userInput
  } = useDispatchersList(handleAddDispatcher, workspaceID)

  const searchResults = useMemberSearch({
    selectedUsers: dispatchersList.map((user) => ({ user })),
    userInput
  })

  const removeDispatcher = (clickedUser: UserDetails) => {
    const { id } = clickedUser
    const dispatcherWorkspaceID = dispatchers.find(({ user }) => user.id === id)?.workspace?.id || ''
    handleRemoveDispatcher({ dispatcherWorkspaceID: dispatcherWorkspaceID, userID: id })
  }

  const assignDispatcher = (workspaceID: string) => {
    handleAssignDispatcher(workspaceID)
  }

  if (openInvitePopup) {
    return (
      <>
        <EmailPhoneForm onSubmit={handleSubmit} loading={loading} initialValue={userInput} />
        {dispatcherWorkspaceList.length > 0 && (
          <WorkspaceListModal
            workspaceList={dispatcherWorkspaceList}
            onSelectWorkspace={assignDispatcher}
            onClose={() => resetInviteState()}
            description="This dispatcher belongs to multiple workspaces. Please select one"
          />
        )}
      </>
    )
  }

  return (
    <MemberSelectionList
      members={searchResults}
      onSearchInput={setUserInput}
      selectedMembers={dispatchersList}
      isLoadingMember={mutationLoading}
      onInviteClick={() => setOpenInvitePopup(true)}
      onMemberSelect={removeDispatcher}
      onMemberRemove={removeDispatcher}
      membersWorkspace={dispatchersWorkspace}
    />
  )
}
